import { useEffect, useId, useState } from 'react';
import { MAX_CANVAS_DIMENSION, MAX_CANVAS_PIXELS } from '../config';
import { LED_COLOR_PRESETS, PROJECTION_COLOR_PRESETS, RESOLUTION_PRESETS } from '../presets';
import type { GeneratorConfig } from '../types';
import { parseNumberDraft, validateHexColor, validateNumberDraft } from '../validation';

interface ControlsProps {
  config: GeneratorConfig;
  updateConfig: (updater: (current: GeneratorConfig) => GeneratorConfig) => void;
  onLogoUpload: (file: File) => void;
}

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  integer?: boolean;
}

export function Controls({ config, updateConfig, onLogoUpload }: ControlsProps) {
  const updateProjection = (patch: Partial<GeneratorConfig['projection']>) =>
    updateConfig((current) => ({
      ...current,
      projection: { ...current.projection, ...patch },
    }));
  const updateLed = (patch: Partial<GeneratorConfig['led']>) =>
    updateConfig((current) => ({ ...current, led: { ...current.led, ...patch } }));
  const updatePower = (patch: Partial<GeneratorConfig['power']>) =>
    updateConfig((current) => ({ ...current, power: { ...current.power, ...patch } }));
  const updateLabel = (patch: Partial<GeneratorConfig['label']>) =>
    updateConfig((current) => ({ ...current, label: { ...current.label, ...patch } }));
  const updateLogo = (patch: Partial<GeneratorConfig['logo']>) =>
    updateConfig((current) => ({ ...current, logo: { ...current.logo, ...patch } }));
  const unitFactor = config.displayUnit === 'm' ? 1000 : 1;
  const unitStep = config.displayUnit === 'm' ? 0.001 : 1;
  const maximumMeasurement = Number.MAX_SAFE_INTEGER / unitFactor;
  const maximumProjectionWidth = Math.min(
    MAX_CANVAS_DIMENSION,
    Math.floor(MAX_CANVAS_PIXELS / config.projection.resolution.height),
  );
  const maximumProjectionHeight = Math.min(
    MAX_CANVAS_DIMENSION,
    Math.floor(MAX_CANVAS_PIXELS / config.projection.resolution.width),
  );
  const ledPixelCount =
    config.led.cabinetResolution.width *
    config.led.cabinetResolution.height *
    config.led.columns *
    config.led.rows;
  const maximumCabinetWidth = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.columns),
    Math.floor(
      (MAX_CANVAS_PIXELS * config.led.cabinetResolution.width) / Math.max(1, ledPixelCount),
    ),
  );
  const maximumCabinetHeight = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.rows),
    Math.floor(
      (MAX_CANVAS_PIXELS * config.led.cabinetResolution.height) / Math.max(1, ledPixelCount),
    ),
  );
  const maximumColumns = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.cabinetResolution.width),
    Math.floor((MAX_CANVAS_PIXELS * config.led.columns) / Math.max(1, ledPixelCount)),
  );
  const maximumRows = Math.min(
    Math.floor(MAX_CANVAS_DIMENSION / config.led.cabinetResolution.height),
    Math.floor((MAX_CANVAS_PIXELS * config.led.rows) / Math.max(1, ledPixelCount)),
  );

  return (
    <div className="controls" aria-label="Pattern settings">
      <section className="control-section control-section--intro">
        <div className="segmented" aria-label="Pattern mode">
          <button
            className={config.mode === 'led' ? 'is-active' : ''}
            type="button"
            onClick={() => updateConfig((current) => ({ ...current, mode: 'led' }))}
          >
            LED wall
          </button>
          <button
            className={config.mode === 'projection' ? 'is-active' : ''}
            type="button"
            onClick={() => updateConfig((current) => ({ ...current, mode: 'projection' }))}
          >
            Projection
          </button>
        </div>
        <label className="field">
          <span>Screen / output name</span>
          <input
            value={config.title}
            maxLength={120}
            onChange={(event) =>
              updateConfig((current) => ({ ...current, title: event.target.value }))
            }
          />
        </label>
        <label className="field">
          <span>Physical size display</span>
          <select
            value={config.displayUnit}
            onChange={(event) =>
              updateConfig((current) => ({
                ...current,
                displayUnit: event.target.value as GeneratorConfig['displayUnit'],
              }))
            }
          >
            <option value="mm">Millimetres (mm)</option>
            <option value="m">Metres (m)</option>
          </select>
        </label>
      </section>

      {config.mode === 'projection' ? (
        <section className="control-section">
          <SectionHeading title="Projection geometry" badge="Physical grid" />
          <label className="field">
            <span>Resolution preset</span>
            <select
              value={presetValue(
                config.projection.resolution.width,
                config.projection.resolution.height,
              )}
              onChange={(event) => {
                const preset = RESOLUTION_PRESETS.find(
                  (item) => `${item.width}x${item.height}` === event.target.value,
                );
                if (preset?.width && preset.height) {
                  updateProjection({ resolution: { width: preset.width, height: preset.height } });
                }
              }}
            >
              {RESOLUTION_PRESETS.map((preset) => (
                <option key={preset.label} value={`${preset.width}x${preset.height}`}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>
          <div className="field-row">
            <NumberField
              label="Width (px)"
              value={config.projection.resolution.width}
              min={1}
              max={maximumProjectionWidth}
              step={1}
              integer
              onChange={(width) =>
                updateProjection({ resolution: { ...config.projection.resolution, width } })
              }
            />
            <NumberField
              label="Height (px)"
              value={config.projection.resolution.height}
              min={1}
              max={maximumProjectionHeight}
              step={1}
              integer
              onChange={(height) =>
                updateProjection({ resolution: { ...config.projection.resolution, height } })
              }
            />
          </div>
          <div className="field-row">
            <NumberField
              label={`Physical width (${config.displayUnit})`}
              value={config.projection.physical.widthMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateProjection({
                  physical: { ...config.projection.physical, widthMm: value * unitFactor },
                })
              }
            />
            <NumberField
              label={`Physical height (${config.displayUnit})`}
              value={config.projection.physical.heightMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateProjection({
                  physical: { ...config.projection.physical, heightMm: value * unitFactor },
                })
              }
            />
          </div>
          <NumberField
            label="Grid step"
            value={config.projection.gridStepMm / unitFactor}
            min={unitStep}
            max={maximumMeasurement}
            step={unitStep}
            suffix={config.displayUnit}
            onChange={(value) => updateProjection({ gridStepMm: value * unitFactor })}
          />
        </section>
      ) : (
        <section className="control-section">
          <SectionHeading title="LED wall geometry" badge="2 px borders" />
          <div className="field-row">
            <NumberField
              label="Cabinet width (px)"
              value={config.led.cabinetResolution.width}
              min={1}
              max={maximumCabinetWidth}
              step={1}
              integer
              onChange={(width) =>
                updateLed({ cabinetResolution: { ...config.led.cabinetResolution, width } })
              }
            />
            <NumberField
              label="Cabinet height (px)"
              value={config.led.cabinetResolution.height}
              min={1}
              max={maximumCabinetHeight}
              step={1}
              integer
              onChange={(height) =>
                updateLed({ cabinetResolution: { ...config.led.cabinetResolution, height } })
              }
            />
          </div>
          <div className="field-row">
            <NumberField
              label={`Cabinet width (${config.displayUnit})`}
              value={config.led.cabinetPhysical.widthMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateLed({
                  cabinetPhysical: { ...config.led.cabinetPhysical, widthMm: value * unitFactor },
                })
              }
            />
            <NumberField
              label={`Cabinet height (${config.displayUnit})`}
              value={config.led.cabinetPhysical.heightMm / unitFactor}
              min={unitStep}
              max={maximumMeasurement}
              step={unitStep}
              onChange={(value) =>
                updateLed({
                  cabinetPhysical: { ...config.led.cabinetPhysical, heightMm: value * unitFactor },
                })
              }
            />
          </div>
          <div className="field-row">
            <NumberField
              label="Columns"
              value={config.led.columns}
              min={1}
              max={maximumColumns}
              step={1}
              integer
              onChange={(columns) => updateLed({ columns })}
            />
            <NumberField
              label="Rows"
              value={config.led.rows}
              min={1}
              max={maximumRows}
              step={1}
              integer
              onChange={(rows) => updateLed({ rows })}
            />
          </div>
        </section>
      )}

      <section className="control-section">
        <SectionHeading title="Pattern" />
        {config.mode === 'projection' ? (
          <>
            <label className="field">
              <span>Projection color preset</span>
              <select
                value={config.projection.colorPreset}
                onChange={(event) => {
                  const preset = PROJECTION_COLOR_PRESETS.find(
                    (item) => item.id === event.target.value,
                  );
                  if (!preset) return;
                  updateProjection({
                    colorPreset: preset.id,
                    ...(preset.id === 'custom'
                      ? {}
                      : {
                          backgroundColorA: preset.backgroundA,
                          backgroundColorB: preset.backgroundB,
                          gridColor: preset.grid,
                          overlayColor: preset.overlay,
                        }),
                  });
                }}
              >
                {PROJECTION_COLOR_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Background</span>
              <select
                value={config.projection.backgroundMode}
                onChange={(event) =>
                  updateProjection({
                    backgroundMode: event.target
                      .value as GeneratorConfig['projection']['backgroundMode'],
                  })
                }
              >
                <option value="solid">Solid</option>
                <option value="checkerboard">Checkerboard</option>
                <option value="transparent">Transparent</option>
              </select>
            </label>
            <ColorField
              label={
                config.projection.backgroundMode === 'checkerboard' ? 'Background A' : 'Background'
              }
              value={config.projection.backgroundColorA}
              onChange={(backgroundColorA) =>
                updateProjection({ backgroundColorA, colorPreset: 'custom' })
              }
            />
            {config.projection.backgroundMode === 'checkerboard' && (
              <ColorField
                label="Background B"
                value={config.projection.backgroundColorB}
                onChange={(backgroundColorB) =>
                  updateProjection({ backgroundColorB, colorPreset: 'custom' })
                }
              />
            )}
            <ColorField
              label="Grid color"
              value={config.projection.gridColor}
              onChange={(gridColor) => updateProjection({ gridColor, colorPreset: 'custom' })}
            />
            <NumberField
              label="Grid line width"
              value={config.projection.gridLineWidth}
              min={1}
              max={32}
              step={1}
              integer
              suffix="px"
              onChange={(gridLineWidth) => updateProjection({ gridLineWidth })}
            />
            <div className="toggle-grid">
              <Toggle
                label="Diagonals"
                checked={config.projection.showDiagonals}
                onChange={(showDiagonals) => updateProjection({ showDiagonals })}
              />
              <Toggle
                label="Center cross"
                checked={config.projection.showCenter}
                onChange={(showCenter) => updateProjection({ showCenter })}
              />
              <Toggle
                label="Circle"
                checked={config.projection.showCircle}
                onChange={(showCircle) => updateProjection({ showCircle })}
              />
            </div>
            <ColorField
              label="Overlay color"
              value={config.projection.overlayColor}
              onChange={(overlayColor) => updateProjection({ overlayColor, colorPreset: 'custom' })}
            />
          </>
        ) : (
          <>
            <label className="field">
              <span>LED color preset</span>
              <select
                value={config.led.colorPreset}
                onChange={(event) => {
                  const preset = LED_COLOR_PRESETS.find((item) => item.id === event.target.value);
                  if (!preset) return;
                  updateLed({
                    colorPreset: preset.id,
                    ...(preset.id === 'custom'
                      ? {}
                      : { backgroundColorA: preset.a, backgroundColorB: preset.b }),
                  });
                }}
              >
                {LED_COLOR_PRESETS.map((preset) => (
                  <option key={preset.id} value={preset.id}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </label>
            <ColorField
              label="Checker color A"
              value={config.led.backgroundColorA}
              onChange={(backgroundColorA) =>
                updateLed({ backgroundColorA, colorPreset: 'custom' })
              }
            />
            <ColorField
              label="Checker color B"
              value={config.led.backgroundColorB}
              onChange={(backgroundColorB) =>
                updateLed({ backgroundColorB, colorPreset: 'custom' })
              }
            />
            <ColorField
              label="Grid color"
              value={config.led.gridColor}
              onChange={(gridColor) => updateLed({ gridColor, colorPreset: 'custom' })}
            />
            <Toggle
              label="Cabinet coordinates (X,Y)"
              checked={config.led.showCoordinates}
              onChange={(showCoordinates) => updateLed({ showCoordinates })}
            />
            {config.led.showCoordinates && (
              <ColorField
                label="Coordinate color"
                value={config.led.coordinateColor}
                onChange={(coordinateColor) =>
                  updateLed({ coordinateColor, colorPreset: 'custom' })
                }
              />
            )}
          </>
        )}
      </section>

      {config.mode === 'led' && (
        <section className="control-section power-controls">
          <SectionHeading title="Power cabling" badge={`${config.power.routes.length} routes`} />
          <p className="control-hint">
            Select <strong>Cable</strong> above the canvas, then click cabinets in power-flow order.
          </p>
          <ColorField
            label="Line color"
            value={config.power.lineColor}
            onChange={(lineColor) => updatePower({ lineColor })}
          />
          <NumberField
            label="Line width"
            value={config.power.lineWidth}
            min={2}
            max={80}
            step={1}
            integer
            suffix="px"
            onChange={(lineWidth) => updatePower({ lineWidth })}
          />
          <Toggle
            label="Incoming power cable"
            checked={config.power.incoming}
            onChange={(incoming) => updatePower({ incoming })}
          />
          {config.power.incoming && (
            <label className="field power-label-field">
              <span>Incoming cable number</span>
              <input
                value={config.power.cableLabel}
                maxLength={32}
                placeholder="PWR-01"
                onChange={(event) => updatePower({ cableLabel: event.target.value })}
              />
            </label>
          )}
          {config.power.routes.length > 0 && (
            <div className="route-list" aria-label="Power routes">
              {config.power.routes.map((route, index) => (
                <div className="route-item" key={route.id}>
                  <span className="route-swatch" style={{ background: route.color }} />
                  <span>
                    <strong>
                      {route.incoming
                        ? route.cableLabel || `Input ${index + 1}`
                        : `Route ${index + 1}`}
                    </strong>
                    <small>
                      {route.points.length} cabinet{route.points.length === 1 ? '' : 's'}
                    </small>
                  </span>
                  <button
                    type="button"
                    aria-label={`Delete route ${index + 1}`}
                    title="Delete route"
                    onClick={() =>
                      updatePower({
                        routes: config.power.routes.filter((item) => item.id !== route.id),
                      })
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="button button--ghost button--danger clear-routes"
                onClick={() => updatePower({ routes: [] })}
              >
                Clear all routes
              </button>
            </div>
          )}
        </section>
      )}

      <section className="control-section">
        <SectionHeading title="Center label" />
        <Toggle
          label="Show label"
          checked={config.label.enabled}
          onChange={(enabled) => updateLabel({ enabled })}
        />
        {config.label.enabled && (
          <>
            <ColorField
              label="Text color"
              value={config.label.textColor}
              onChange={(textColor) => updateLabel({ textColor })}
            />
            <NumberField
              label="Font size"
              value={config.label.fontSize}
              min={10}
              max={500}
              step={1}
              integer
              suffix="px"
              onChange={(fontSize) => updateLabel({ fontSize })}
            />
            <ColorField
              label="Label background"
              value={config.label.backgroundColor}
              onChange={(backgroundColor) => updateLabel({ backgroundColor })}
            />
            <RangeField
              label="Background opacity"
              value={config.label.backgroundOpacity}
              onChange={(backgroundOpacity) => updateLabel({ backgroundOpacity })}
            />
          </>
        )}
      </section>

      <section className="control-section">
        <SectionHeading title="Company logo" badge="PNG" />
        <label className="file-picker">
          <input
            type="file"
            accept="image/png"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onLogoUpload(file);
              event.target.value = '';
            }}
          />
          <span>{config.logo.fileName || 'Choose a transparent PNG'}</span>
        </label>
        {config.logo.dataUrl && (
          <>
            <RangeField
              label="Logo size"
              value={config.logo.scale}
              min={0.02}
              max={0.3}
              step={0.01}
              onChange={(scale) => updateLogo({ scale })}
            />
            <RangeField
              label="Logo opacity"
              value={config.logo.opacity}
              onChange={(opacity) => updateLogo({ opacity })}
            />
            <button
              type="button"
              className="button button--ghost button--danger"
              onClick={() => updateLogo({ dataUrl: null, fileName: null })}
            >
              Remove logo
            </button>
          </>
        )}
      </section>
    </div>
  );
}

function SectionHeading({ title, badge }: { title: string; badge?: string }) {
  return (
    <div className="section-heading">
      <h2>{title}</h2>
      {badge && <span className="badge">{badge}</span>}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  integer = false,
}: NumberFieldProps) {
  const [draft, setDraft] = useState(String(value));
  const errorId = useId();
  const error = validateNumberDraft(draft, { min, max, integer });

  useEffect(() => {
    const timer = window.setTimeout(() => setDraft(String(value)), 0);
    return () => window.clearTimeout(timer);
  }, [value]);

  return (
    <label className="field">
      <span>{label}</span>
      <div className="input-with-suffix">
        <input
          type="number"
          value={draft}
          min={min}
          max={max}
          step={step}
          inputMode="decimal"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            const nextDraft = event.target.value;
            setDraft(nextDraft);
            const parsed = parseNumberDraft(nextDraft, { min, max, integer });
            if (parsed !== null) onChange(parsed);
          }}
          onBlur={() => {
            if (error) setDraft(String(value));
          }}
        />
        {suffix && <span>{suffix}</span>}
      </div>
      {error && (
        <small className="field-error" id={errorId}>
          {error}
        </small>
      )}
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const normalizedValue = value.toUpperCase();
  const [draft, setDraft] = useState(normalizedValue);
  const errorId = useId();
  const error = validateHexColor(draft);

  useEffect(() => {
    const timer = window.setTimeout(() => setDraft(normalizedValue), 0);
    return () => window.clearTimeout(timer);
  }, [normalizedValue]);

  return (
    <label className="field color-field">
      <span>{label}</span>
      <span className="color-input">
        <input
          type="color"
          value={value}
          onChange={(event) => {
            const nextValue = event.target.value.toUpperCase();
            setDraft(nextValue);
            onChange(nextValue);
          }}
        />
        <input
          className="color-text"
          value={draft}
          maxLength={7}
          spellCheck={false}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => {
            const nextDraft = event.target.value.toUpperCase();
            setDraft(nextDraft);
            if (!validateHexColor(nextDraft)) onChange(nextDraft);
          }}
          onBlur={() => {
            if (error) setDraft(normalizedValue);
          }}
          aria-label={`${label} hex value`}
        />
      </span>
      {error && (
        <small className="field-error color-field-error" id={errorId}>
          {error}
        </small>
      )}
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle-track" aria-hidden="true" />
      <span>{label}</span>
    </label>
  );
}

function RangeField({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <label className="field range-field">
      <span>
        {label} <output>{Math.round(value * 100)}%</output>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => {
          const nextValue = Number(event.target.value);
          if (Number.isFinite(nextValue)) onChange(Math.min(max, Math.max(min, nextValue)));
        }}
      />
    </label>
  );
}

function presetValue(width: number, height: number): string {
  const value = `${width}x${height}`;
  return RESOLUTION_PRESETS.some((preset) => `${preset.width}x${preset.height}` === value)
    ? value
    : '0x0';
}
